export const UI_THEME = {
  colors: {
    // Backgrounds — anthracite charcoal (warm neutral, no blue undertone)
    bg: '#0e0e10',
    canvas: '#141416',
    rail: '#0f0f11',

    // Surfaces — each level visibly distinct
    surface0: '#1c1c1f',
    surface1: '#222226',
    surface2: '#2a2a2f',

    // Backward-compat aliases → map to surface levels
    panel: '#1c1c1f',
    panelAlt: '#141416',
    panelSoft: 'rgba(34, 34, 38, 0.72)',

    // Text — warm cream/neutral, no cool blue cast
    text: '#f0eee9',
    textSecondary: '#b8b3a8',
    textTertiary: '#88827a',
    textGhost: '#5c5650',
    // UI elevation 2026-06-09: label token for eyebrow/kicker roles (4.61:1 AA contrast)
    label: '#81786d',

    // Backward-compat aliases
    textMuted: '#b8b3a8',
    textSoft: '#88827a',
    textDim: '#7a7468',

    // Borders — warm low-saturation
    border: 'rgba(180, 168, 150, 0.08)',
    borderStrong: 'rgba(180, 168, 150, 0.14)',
    borderSubtle: 'rgba(180, 168, 150, 0.06)',

    // Backward-compat aliases
    line: 'rgba(180, 168, 150, 0.08)',
    lineStrong: 'rgba(180, 168, 150, 0.14)',

    // Brand — deep ember orange
    brand: '#d4582d',
    brandSoft: 'rgba(212, 88, 45, 0.12)',
    brandText: '#f59169',

    // Functional — accent (warm amber, complementary to brand)
    accent: '#e8985a',
    accentSoft: 'rgba(232, 152, 90, 0.12)',
    accentStrong: '#f4ba8a',

    success: '#5cba80',
    successSoft: 'rgba(92, 186, 128, 0.12)',
    warning: '#e0b550',
    warningSoft: 'rgba(224, 181, 80, 0.12)',
    error: '#d47270',
    errorSoft: 'rgba(212, 114, 112, 0.10)',

    neutralSoft: 'rgba(140, 132, 116, 0.10)',
  },
  // Radii scale — Wave 47x. Previously md/lg/xl all collapsed to 8px,
  // which destroyed the size→roundness hierarchy across the app. Now
  // the scale is monotonic (xs 4 → 2xl 18) and matches the studio.css
  // --radius-* tokens. The original "8 everywhere" was a regression
  // from earlier polish; this restores discrete steps.
  radii: {
    xs: 4,
    sm: 6,
    md: 8,
    lg: 10,
    xl: 14,
    '2xl': 18,
    pill: 999,
  },
  shadow: {
    // Layered shadows mirroring studio.css --shadow-* tokens. World-class
    // apps stack a tight contact shadow + a soft ambient — single hard
    // shadows always read as "stamped on" instead of "lifted off".
    card: '0 1px 2px rgba(0, 0, 0, 0.32), 0 2px 4px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06)',
    elevated:
      '0 4px 8px rgba(0, 0, 0, 0.32), 0 12px 24px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.06)',
    floating:
      '0 8px 16px rgba(0, 0, 0, 0.36), 0 24px 48px rgba(0, 0, 0, 0.36), 0 0 0 1px rgba(0, 0, 0, 0.06)',
    brand:
      '0 4px 14px rgba(212, 88, 45, 0.28), 0 1px 3px rgba(212, 88, 45, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
    // Highlight — top-edge sheen for raised surfaces.
    highlight: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)',
    highlightSoft: 'inset 0 1px 0 rgba(255, 255, 255, 0.025)',
  },
  // Motion token mirror — matches studio.css. Using these in inline
  // styles gives a single tunable that propagates to JS-rendered
  // surfaces too, not just CSS-class-styled ones.
  motion: {
    instant: '60ms',
    quick: '120ms',
    base: '220ms',
    slow: '360ms',
    luxe: '520ms',
  },
  ease: {
    snap: 'cubic-bezier(0.4, 0, 0.2, 1)',
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    glide: 'cubic-bezier(0.22, 1, 0.36, 1)',
  },
  fonts: {
    body: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
    mono: "'DM Mono', 'SF Mono', Menlo, monospace",
    editorial: "'Fraunces', 'Plus Jakarta Sans', Georgia, serif",
  },
}

const TONES = {
  neutral: {
    background: UI_THEME.colors.neutralSoft,
    color: UI_THEME.colors.textSecondary,
    borderColor: 'rgba(180, 168, 150, 0.16)',
  },
  success: {
    background: UI_THEME.colors.successSoft,
    color: UI_THEME.colors.success,
    borderColor: 'rgba(92, 186, 128, 0.22)',
  },
  error: {
    background: UI_THEME.colors.errorSoft,
    color: UI_THEME.colors.error,
    borderColor: 'rgba(212, 114, 112, 0.22)',
  },
  experimental: {
    background: UI_THEME.colors.warningSoft,
    color: UI_THEME.colors.warning,
    borderColor: 'rgba(224, 181, 80, 0.22)',
  },
  planning: {
    background: UI_THEME.colors.accentSoft,
    color: UI_THEME.colors.accentStrong,
    borderColor: 'rgba(232, 152, 90, 0.22)',
  },
  muted: {
    background: 'rgba(28, 28, 31, 0.72)',
    color: UI_THEME.colors.textTertiary,
    borderColor: 'rgba(140, 132, 116, 0.16)',
  },
  manual: {
    background: 'rgba(180, 168, 150, 0.08)',
    color: '#b8b3a8',
    borderColor: 'rgba(180, 168, 150, 0.14)',
  },
  quarantined: {
    background: 'rgba(212, 114, 112, 0.06)',
    color: '#c4847e',
    borderColor: 'rgba(212, 114, 112, 0.14)',
  },
}

const BUTTONS = {
  primary: {
    background: UI_THEME.colors.brand,
    // Deep brown text reads cleanly on the orange brand pill.
    color: '#1a0e05',
    borderColor: 'rgba(212, 88, 45, 0.5)',
  },
  outline: {
    background: UI_THEME.colors.surface0,
    color: UI_THEME.colors.textSecondary,
    borderColor: UI_THEME.colors.border,
  },
  ghost: {
    background: 'transparent',
    color: UI_THEME.colors.textTertiary,
    borderColor: 'transparent',
  },
  danger: {
    background: 'transparent',
    color: UI_THEME.colors.error,
    borderColor: 'transparent',
  },
  teal: {
    background: UI_THEME.colors.successSoft,
    color: '#afe8c5',
    borderColor: 'rgba(92, 186, 128, 0.24)',
  },
  // Legacy "blue" key — now the warm-amber accent variant.
  // Renaming would touch many call sites; the visual swap is enough.
  blue: {
    background: UI_THEME.colors.accentSoft,
    color: UI_THEME.colors.accentStrong,
    borderColor: 'rgba(232, 152, 90, 0.24)',
  },
}

export function getToneStyles(tone = 'neutral') {
  return TONES[tone] || TONES.neutral
}

export function getButtonVariant(variant = 'outline') {
  return BUTTONS[variant] || BUTTONS.outline
}

export default {
  UI_THEME,
  getToneStyles,
  getButtonVariant,
}
